import { formatLocalDateTime } from '../utils/localDateTime';

import React, { useState, useEffect, useCallback } from 'react';
import { generateVcmcInitialState } from '@common/services/geminiService';
import type { VcmcDashboardState } from '@types';
import Loader from './Loader';
import { ICONS } from './constants';
import { db } from '../../firebaseConfig';
import { collection, query, orderBy, limit, getDocs, where } from 'firebase/firestore';

interface VCMCSetupProps {
    onSimulationStart: (initialState: VcmcDashboardState) => void;
}

interface LiveEvent {
    id: string;
    title: string;
    iso3: string;
    countryName: string;
    riskScore: number;
    summary: string;
    category: string;
    processedAt: string;
}

const RISK_COLORS: Record<number, string> = {};
const getRiskColor = (score: number) => {
    if (score >= 85) return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', badge: 'bg-red-600' };
    if (score >= 70) return { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200', badge: 'bg-orange-500' };
    return { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', badge: 'bg-amber-500' };
};

const VCMCSetup: React.FC<VCMCSetupProps> = ({ onSimulationStart }) => {
    const [seedMode, setSeedMode] = useState<'manual' | 'live'>('manual');
    const [scenario, setScenario] = useState<string>('A coordinated cyber attack has targeted the national power grid, causing widespread outages in major metropolitan areas.');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Live event state
    const [liveEvents, setLiveEvents] = useState<LiveEvent[]>([]);
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<LiveEvent | null>(null);

    const loadLiveEvents = useCallback(async () => {
        setLoadingEvents(true);
        try {
            const q = query(
                collection(db, 'risk_events'),
                orderBy('processedAt', 'desc'),
                limit(30)
            );
            const snap = await getDocs(q);
            const events: LiveEvent[] = snap.docs
                .map(d => {
                    const data = d.data();
                    const score = data.analysis?.risk_score ?? 0;
                    return {
                        id: d.id,
                        title: data.analysis?.intel_title || data.title || 'Untitled Event',
                        iso3: data.geo?.country_iso3 || 'UNK',
                        countryName: data.geo?.location_name || data.geo?.country_iso3 || 'Unknown',
                        riskScore: score,
                        summary: data.analysis?.incident_summary || data.content || 'No summary available.',
                        category: data.analysis?.category || 'Intelligence',
                        processedAt: data.processedAt?.toDate
                            ? formatLocalDateTime(data.processedAt.toDate())
                            : 'Recent'
                    };
                })
                .filter(e => e.riskScore >= 70)
                .slice(0, 10);
            setLiveEvents(events);
        } catch (err: any) {
            console.error('VCMC: Failed to load live events', err);
        } finally {
            setLoadingEvents(false);
        }
    }, []);

    useEffect(() => {
        if (seedMode === 'live' && liveEvents.length === 0) {
            loadLiveEvents();
        }
    }, [seedMode, liveEvents.length, loadLiveEvents]);

    const handleEventSelect = (event: LiveEvent) => {
        setSelectedEvent(event);
        setScenario(
            `LIVE INTELLIGENCE EVENT — ${event.category.toUpperCase()} [Risk Score: ${event.riskScore}/100]\n` +
            `Country: ${event.countryName} (${event.iso3}) | ${event.processedAt}\n\n` +
            `Event: ${event.title}\n\n` +
            `Incident Summary: ${event.summary}\n\n` +
            `Simulate a crisis management response to this live intelligence event using VCMC protocols.`
        );
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!scenario.trim()) {
            setError("Please provide a crisis scenario.");
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            // Fetch the latest Organizational Risk Footprint (DailyEnterpriseAudit)
            let orgRiskFootprint = null;
            try {
                const auditQuery = query(
                    collection(db, 'daily_enterprise_audits'),
                    orderBy('timestamp', 'desc'),
                    limit(1)
                );
                const auditSnapshot = await getDocs(auditQuery);
                if (!auditSnapshot.empty) {
                    orgRiskFootprint = auditSnapshot.docs[0].data();
                }
            } catch (auditError) {
                console.warn("VCMC SETUP - Failed to fetch org footprint:", auditError);
            }

            const initialState = await generateVcmcInitialState(scenario, orgRiskFootprint);
            onSimulationStart(initialState);
        } catch (e: any) {
            setError(e.message || "Failed to start simulation. Please try again.");
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-lg max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center text-white flex-shrink-0">
                    {ICONS.logo}
                </div>
                <div>
                    <h2 className="text-2xl font-bold text-slate-800">VCMC Simulation Setup</h2>
                    <p className="text-slate-500 text-sm">Define the initial crisis scenario for your Virtual Crisis Management Center.</p>
                </div>
            </div>

            {/* Mode Toggle */}
            <div className="mt-6 mb-5 flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit">
                <button
                    type="button"
                    id="vcmc-mode-manual"
                    onClick={() => { setSeedMode('manual'); setSelectedEvent(null); }}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                        seedMode === 'manual'
                            ? 'bg-white text-slate-800 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                    }`}
                >
                    ✏️ Manual Scenario
                </button>
                <button
                    type="button"
                    id="vcmc-mode-live"
                    onClick={() => setSeedMode('live')}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${
                        seedMode === 'live'
                            ? 'bg-white text-slate-800 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                    }`}
                >
                    <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    Seed from Live Event
                </button>
            </div>

            <form onSubmit={handleSubmit}>
                {/* Live Event Picker */}
                {seedMode === 'live' && (
                    <div className="mb-5">
                        <p className="text-sm font-medium text-slate-600 mb-2">
                            Select a high-priority live intelligence event to seed the simulation:
                        </p>
                        {loadingEvents ? (
                            <div className="flex items-center gap-2 text-slate-500 text-sm py-4">
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                                </svg>
                                Loading live intelligence events...
                            </div>
                        ) : liveEvents.length === 0 ? (
                            <div className="text-sm text-slate-500 bg-slate-50 rounded-lg p-4 border border-slate-200">
                                No high-priority events (Risk Score ≥ 70) found in the last 24 hours. Use Manual Scenario mode.
                            </div>
                        ) : (
                            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                                {liveEvents.map(evt => {
                                    const colors = getRiskColor(evt.riskScore);
                                    const isSelected = selectedEvent?.id === evt.id;
                                    return (
                                        <button
                                            type="button"
                                            key={evt.id}
                                            id={`vcmc-event-${evt.id}`}
                                            onClick={() => handleEventSelect(evt)}
                                            className={`w-full text-left rounded-lg border p-3 transition-all ${
                                                isSelected
                                                    ? 'border-slate-900 bg-slate-900 text-white'
                                                    : `${colors.border} ${colors.bg} hover:border-slate-400`
                                            }`}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1 min-w-0">
                                                    <p className={`text-sm font-semibold truncate ${isSelected ? 'text-white' : 'text-slate-800'}`}>
                                                        {evt.title}
                                                    </p>
                                                    <p className={`text-xs mt-0.5 truncate ${isSelected ? 'text-slate-300' : 'text-slate-500'}`}>
                                                        {evt.category} · {evt.countryName} · {evt.processedAt}
                                                    </p>
                                                </div>
                                                <span className={`flex-shrink-0 text-xs font-bold px-2 py-0.5 rounded-full text-white ${isSelected ? 'bg-white text-slate-900' : colors.badge}`}>
                                                    {evt.riskScore}
                                                </span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* Scenario Preview/Editor */}
                <div className="space-y-4">
                    <div>
                        <label htmlFor="vcmc-scenario" className="block text-sm font-medium text-slate-600 mb-1">
                            {seedMode === 'live' && selectedEvent
                                ? '✅ Scenario seeded — review & edit before launching'
                                : 'Crisis Scenario'}
                        </label>
                        <textarea
                            id="vcmc-scenario"
                            name="scenario"
                            value={scenario}
                            onChange={(e) => setScenario(e.target.value)}
                            placeholder="Describe the crisis scenario..."
                            className={`w-full border rounded-lg p-3 focus:ring-2 focus:ring-slate-700 focus:outline-none transition-colors text-slate-800 text-sm ${
                                selectedEvent && seedMode === 'live'
                                    ? 'bg-slate-50 border-slate-300'
                                    : 'bg-slate-50 border-slate-300'
                            }`}
                            rows={5}
                            required
                        />
                    </div>
                </div>

                <div className="mt-6 flex items-center justify-between">
                    <p className="text-xs text-slate-400">
                        Simulation is grounded in your organization's latest risk footprint.
                    </p>
                    <button
                        type="submit"
                        id="vcmc-start-simulation"
                        disabled={isLoading || (seedMode === 'live' && !selectedEvent && liveEvents.length > 0)}
                        className="bg-slate-900 hover:bg-slate-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold py-2 px-6 rounded-lg flex items-center gap-2 transition-colors duration-200"
                    >
                        {isLoading ? (
                            <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                            </svg>
                        ) : (
                            <span>▶</span>
                        )}
                        {isLoading ? 'Initializing...' : 'Launch Simulation'}
                    </button>
                </div>
                {error && <p className="text-red-600 mt-3 text-sm text-right">{error}</p>}
            </form>
            {isLoading && <Loader />}
        </div>
    );
};

export default VCMCSetup;
